import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { Feature, useMap } from "@app/component/map/FeaturedMap";
import TerritoryPopover from "@app/scene/map/components/features/TerritoryFeature/components/TerritoryPopover";
import MapTooltip from "@app/scene/map/components/MapTooltip";
import { ensureTerritoriesHaveShape } from "@app/store/territories/actions";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import useAnalytics from "@app/util/contexts/useAnalytics";
import defaultIdGetter from "@app/util/defaultIdGetter";
import useMapDataEventHandler from "@app/util/hook/map/useMapDataEventHandler";
import { TERRITORY_FEATURE_ZINDEX } from "@app/util/map/consts";

import TerritoryLabelFeature from "./components/TerritoryLabelFeature";
import territoryGeometryGetter from "./utils/territoryGeometryGetter";

const getTerritoriesAtPoint = (point: LongLat, territories: Territory[]): Territory[] =>
  territories.filter((territory) => {
    const { territoryDetail } = territory;
    const geometry =
      territoryDetail.type === "points" ? territoryDetail.points : territoryDetail.shape;
    return geometry && booleanPointInPolygon(point, geometry);
  });

const shouldRedrawTerritory = (a: TerritoryItem, b: TerritoryItem) =>
  a.id !== b.id ||
  a.color !== b.color ||
  a.normalView !== b.normalView ||
  a.highlighted !== b.highlighted ||
  a.territoryDetail.opacity !== b.territoryDetail.opacity ||
  a.territoryDetail.hidden !== b.territoryDetail.hidden ||
  a.territoryDetail.points !== b.territoryDetail.points ||
  a.territoryDetail.shape !== b.territoryDetail.shape;

interface TerritoryItem extends Territory {
  highlighted: boolean;
  normalView: boolean;
  selected: boolean;
}

interface TerritoryFeatureProps {
  disabled?: boolean;
  highlightedTerritoryId?: Territory["id"];
  onOpen?: (territory: Territory) => void;
  selectedTerritoryId?: Territory["id"];
  styleGetter?: (item: Territory) => google.maps.Data.StyleOptions;
  territories: Territory[];
  type?: string;
  viewMode?: boolean;
  zIndex?: number;
}

interface Props extends TerritoryFeatureProps {
  onEnsureTerritoriesHaveShape: typeof ensureTerritoriesHaveShape;
}

const TerritoryFeature: React.FC<Props> = ({
  disabled,
  highlightedTerritoryId,
  onEnsureTerritoriesHaveShape,
  onOpen,
  selectedTerritoryId,
  styleGetter,
  territories,
  type = "territories",
  viewMode,
  zIndex = TERRITORY_FEATURE_ZINDEX,
}) => {
  const intl = useIntl();
  const { map } = useMap();
  const analytics = useAnalytics();

  useEffect(() => {
    const visibleTerritoriesWithoutShapes = territories.filter(
      ({ territoryDetail }) =>
        !territoryDetail.hidden && territoryDetail.type !== "points" && !territoryDetail.shape
    );
    if (visibleTerritoriesWithoutShapes.length) {
      onEnsureTerritoriesHaveShape(visibleTerritoriesWithoutShapes);
    }
  }, [onEnsureTerritoriesHaveShape, territories]);

  const visibleTerritoriesWithShape = useMemo(
    () =>
      territories.filter(
        ({ territoryDetail }) =>
          !territoryDetail.hidden && (territoryDetail.type === "points" || territoryDetail.shape)
      ),
    [territories]
  );

  const handleTerritoryDoubleClick = useCallback(
    (territory: Territory) => {
      if (!disabled) {
        analytics.clicked(["Double Click Territory"], { id: territory.id, name: territory.name });
        onOpen?.(territory);
      }
    },
    [analytics, disabled, onOpen]
  );

  const territoryStyleGetter = useDynamicCallback(
    (territory: TerritoryItem): google.maps.Data.StyleOptions => {
      const color = anyColorToHex(territory.color ?? Color.BLACK);
      const normalView = territory.normalView;

      return {
        clickable: !disabled,
        fillColor: color,
        fillOpacity: normalView
          ? territory.highlighted
            ? 1
            : territory.territoryDetail.opacity
          : viewMode
          ? 0.2
          : 0.4,
        strokeColor: color,
        strokeOpacity: viewMode ? 0.2 : 1,
        strokeWeight: normalView
          ? highlightedTerritoryId === territory.id
            ? 4
            : 2
          : viewMode
          ? 1
          : 2,
        zIndex: territory.selected ? zIndex + 10 : zIndex,
      };
    }
  );

  const territoryItems = useMemo(() => {
    return visibleTerritoriesWithShape.map<TerritoryItem>((territory) => ({
      ...territory,
      highlighted: highlightedTerritoryId === territory.id,
      normalView: !isDefined(selectedTerritoryId) || selectedTerritoryId === territory.id,
      selected: selectedTerritoryId === territory.id,
    }));
  }, [visibleTerritoriesWithShape, highlightedTerritoryId, selectedTerritoryId]);

  const [hoveredTerritories, setHoveredTerritories] = useState<Territory[] | undefined>();
  const [hoverMousePosition, setHoverMousePosition] = useState<google.maps.LatLng | undefined>();
  const handleMouseOver = useCallback(
    (territory: Territory, event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const point: LongLat = [event.latLng!.lng(), event.latLng!.lat()];
        setHoveredTerritories(getTerritoriesAtPoint(point, visibleTerritoriesWithShape));
      } else {
        setHoveredTerritories([territory]);
      }
      setHoverMousePosition(event.latLng!);
    },
    [visibleTerritoriesWithShape]
  );

  const handleMouseLeave = useCallback(() => setHoveredTerritories(undefined), []);
  const handleMouseMove = useDebouncedCallback([
    (territory: Territory, event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const point: LongLat = [event.latLng!.lng(), event.latLng!.lat()];
        // We check if the prev state is undefined because mouseMove event can be fired
        // after mouseLeave. And we don't want to show tooltip in that case. We also
        // assume that mouseMove should only happen after mouseOver, so it should be fine.
        setHoveredTerritories((prevState) =>
          prevState !== undefined
            ? getTerritoriesAtPoint(point, visibleTerritoriesWithShape)
            : prevState
        );
      } else {
        // See comment above about the prevState checking
        setHoveredTerritories((prevState) => (prevState !== undefined ? [territory] : prevState));
      }
      setHoverMousePosition(event.latLng!);
    },
    500,
  ]);

  const [clickedTerritories, setClickedTerritories] = useState<Territory[] | undefined>();
  const [clickMousePosition, setClickMousePosition] = useState<google.maps.LatLng | undefined>();
  const handleTerritoryClick = useCallback(
    (territory: Territory, event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const point: LongLat = [event.latLng!.lng(), event.latLng!.lat()];
        setClickedTerritories(getTerritoriesAtPoint(point, visibleTerritoriesWithShape));
      } else {
        setClickedTerritories([territory]);
      }

      setClickMousePosition(event.latLng!);
    },
    [visibleTerritoriesWithShape]
  );
  const handleHidePopover = useCallback(() => setClickedTerritories(undefined), []);

  useMapDataEventHandler(map, "mouseover", function (event) {
    if (!disabled && hoveredTerritories) {
      map.data.overrideStyle(event.feature, { strokeWeight: 4 });
    }
  });

  useMapDataEventHandler(map, "mouseout", function (event) {
    if (!disabled) {
      map.data.revertStyle(event.feature);
    }
  });

  return (
    <>
      <Feature<TerritoryItem>
        geometryGetter={territoryGeometryGetter}
        idGetter={defaultIdGetter}
        isDifferent={shouldRedrawTerritory}
        items={territoryItems}
        onClick={handleTerritoryClick}
        onDoubleClick={handleTerritoryDoubleClick}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onMouseOver={handleMouseOver}
        styleGetter={styleGetter ?? territoryStyleGetter}
        type={disabled ? `${type}-disabled` : type}
      />
      {!disabled && (
        // Labels are not supported on the Polygon geometries, that's why we display
        // them separately
        <TerritoryLabelFeature
          selectedTerritoryId={selectedTerritoryId}
          territories={visibleTerritoriesWithShape}
          type={`${type}-labels`}
        />
      )}
      {onOpen && hoveredTerritories?.length ? (
        <MapTooltip
          position={hoverMousePosition}
          showArrow={false}
          tooltip={
            hoveredTerritories.length > 1
              ? intl.formatMessage({
                  id: "map.feature.territory.hover.tooltip.multiple",
                  defaultMessage: "Multiple Territories",
                  description:
                    "Territory tooltip on map when there is multiple territories under cursor",
                })
              : intl.formatMessage(
                  {
                    id: "map.feature.territory.hover.tooltip",
                    defaultMessage: `Double click to open ‘{territoryName}’`,
                    description: "Territory tooltip on map",
                  },
                  { territoryName: hoveredTerritories[0].name }
                )
          }
        />
      ) : null}
      {onOpen && clickedTerritories && clickMousePosition && (
        <TerritoryPopover
          onHide={handleHidePopover}
          onOpen={onOpen}
          position={clickMousePosition}
          territories={clickedTerritories}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  onEnsureTerritoriesHaveShape: ensureTerritoriesHaveShape,
};

export default connect(null, mapDispatchToProps)(TerritoryFeature);
